/**
 * TrialBanner — Wave 7 (iter183)
 *
 * Persistent banner shown at the top of every authenticated page for:
 *   - trialing workspaces (progress-style, count-down copy)
 *   - trial_expired / past_due / canceled workspaces (read-only lock)
 *
 * Reads the compact billing manifest from the /api/auth/permissions
 * response via the PermissionsProvider — no extra XHR.
 *
 * Behaviour:
 *   - Hidden entirely for `legacy` and `active` plans (the current
 *     silent-happy-path — no clutter for paid or legacy tenants).
 *   - "Upgrade" CTA routes to /settings/billing (Merchant Admin only
 *     writes there via API; every role can view).
 *   - Trial-only: dismissible for 24h via localStorage so exec users
 *     who've seen it 10 times in one day get a break; on lock states
 *     the banner is NEVER dismissible.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertTriangle, Sparkles, X, Info } from 'lucide-react';
import { usePermissionsContext } from '../lib/permissions';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const DISMISS_KEY = 'trial_banner_dismissed_until';
const DISMISS_MS = 24 * 60 * 60 * 1000; // 24 hours

// UK-specific routes where mentioning HMRC in the sandbox tooltip is
// actually informative (vs alarming for a merchant only using EU).
const UK_CONTEXT_ROUTE_PREFIXES = [
  '/rgr',
  '/historical-audit',
  '/hmrc-oauth',
  '/hmrc-direct-filing',
  '/hmrc-notifications',
  '/imports',
];

function isUkContext(pathname) {
  if (!pathname) return false;
  return UK_CONTEXT_ROUTE_PREFIXES.some((p) => pathname.startsWith(p));
}

export function TrialBanner() {
  const { billing, loading } = usePermissionsContext();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    setDismissed(until > Date.now());
  }, []);

  if (loading || !billing) return null;

  const { plan_status, trial_days_remaining, read_only } = billing;

  // Silent for happy-path states
  if (plan_status === 'active' || plan_status === 'legacy') return null;

  const ukContext = isUkContext(location.pathname);

  // Read-only lock state — always visible, never dismissible
  if (read_only) {
    const isTrialLock = billing?.access_mode === 'trial';
    const copy = {
      trial_expired: isTrialLock
        ? 'Your trial has ended and the workspace is now read-only. Contact your Meridian account manager to continue.'
        : 'Your Early Access sandbox has ended. The workspace is now read-only. Contact your Meridian account manager to continue.',
      past_due: 'An invoice is overdue. The workspace is temporarily read-only. Contact your Meridian account manager to restore access.',
      canceled: 'This workspace is now read-only. Contact your Meridian account manager to restore access.',
    }[plan_status] || 'Your workspace is currently read-only.';
    return (
      <div
        data-testid="trial-banner-locked"
        className="flex items-center justify-between gap-3 rounded-md border border-rose-300 bg-rose-50 px-4 py-2.5 text-sm text-rose-900 mb-4"
      >
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 text-rose-600" />
          <span className="truncate">{copy}</span>
        </div>
      </div>
    );
  }

  // Active Early Access sandbox — count-down banner (dismissible for 24h)
  if (plan_status === 'trialing') {
    if (dismissed) return null;
    const days = trial_days_remaining;
    const urgent = typeof days === 'number' && days <= 3;
    const isTrial = billing?.access_mode === 'trial';
    const modeLabel = isTrial ? 'trial' : 'Early Access sandbox';
    const urgentCta = 'Contact your Meridian account manager';
    const sandboxTooltip = isTrial
      ? 'Your trial workspace has been unlocked by a specialist. Filing and integrations may be enabled. Contact your Meridian account manager to continue after the trial.'
      : ukContext
      ? 'Your Early Access sandbox is invite-only. No real HMRC filing takes place. After the sandbox window the workspace becomes read-only until a specialist converts you to a trial.'
      : 'Your Early Access sandbox is invite-only. No live filings take place. After the sandbox window the workspace becomes read-only until a specialist converts you to a trial.';
    return (
      <div
        data-testid="trial-banner-trialing"
        className={`flex items-center justify-between gap-3 rounded-md border px-4 py-2.5 text-sm mb-4 ${
          urgent
            ? 'border-amber-400 bg-amber-50 text-amber-900'
            : 'border-blue-300 bg-blue-50 text-blue-900'
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className={`w-4 h-4 flex-shrink-0 ${urgent ? 'text-amber-600' : 'text-blue-600'}`} />
          <span className="truncate">
            {days === null || days === undefined ? (
              `${modeLabel}, pricing available on request.`
            ) : days <= 0 ? (
              `Your ${modeLabel} ends today. ${urgentCta}.`
            ) : (
              <>
                <strong data-testid="trial-days-remaining">{days}</strong> day{days === 1 ? '' : 's'} left on your {modeLabel}
                {urgent && `. ${urgentCta}.`}
              </>
            )}
          </span>
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="inline-flex items-center text-current opacity-70 hover:opacity-100"
                  aria-label="What is a sandbox pilot?"
                  data-testid="trial-banner-sandbox-info"
                >
                  <Info className="w-3.5 h-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed" data-testid="trial-banner-sandbox-tooltip">
                {sandboxTooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => {
              localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_MS));
              setDismissed(true);
            }}
            className="text-current opacity-60 hover:opacity-100"
            aria-label="Dismiss for 24h"
            data-testid="trial-banner-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return null;
}

export default TrialBanner;
